import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    type ClipboardEvent as ReactClipboardEvent,
    type KeyboardEvent as ReactKeyboardEvent,
    type SyntheticEvent,
} from 'react';
import { useFramework } from '@/framework';
import { getUtf8ByteLength } from '@/utils/text-kit';
import type { TextEditorPlugin } from '../manager';
import {
    getCaretRect,
    getLineIndexFromOffset,
    resolveAdjacentInlineOffset,
    resolveByteOffsetAtLineInline,
    shouldRestoreTextareaFocus,
    textareaSelectionToTextEditorSelection,
    textEditorSelectionToTextareaSelection,
} from '../utils';
import { useSession } from './use-session';
import './textarea.less';

const ARROW_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);

/**
 * 与旧版一致，用两个全角空格模拟一次 Tab 缩进
 */
const TAB_TEXT = '\u3000\u3000';

export type TextEditorTextareaRef = {
    exitEditing: () => Promise<void> | void;
    resetDesiredInline: () => void;
    /**
     * 用独立输入框承接原生菜单，保持正文输入框的内容和尺寸
     *
     * @param point - 右键事件的客户端坐标
     */
    prepareContextMenu: (point: { clientX: number; clientY: number }) => void;
};

type Props = {
    minHeight: number;
    onKeyDown: (event: ReactKeyboardEvent<HTMLTextAreaElement>) => void;
};

export const TextEditorTextarea = forwardRef<TextEditorTextareaRef, Props>(({ minHeight, onKeyDown }, ref) => {
    const framework = useFramework();
    const textEditor = framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');
    const session = useSession();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const desiredInlineRef = useRef<number | null>(null);
    const composingRef = useRef(false);
    const lastSessionIDRef = useRef<number | null>(null);
    const restoreContextMenuRef = useRef<(() => void) | null>(null);
    const disposeContextMenuRef = useRef<(() => void) | null>(null);

    /**
     * 收束输入法组合态：composition 期间 textarea 的 DOM 值可能已经变化，
     * 但编辑器还没落一次最终变更，退出编辑前也需要用真实选区补齐。
     */
    const flushTextareaComposition = useCallback(() => {
        const current = textEditor.getSession();
        const textarea = textareaRef.current;
        if (!current || !textarea || !composingRef.current) return;

        const range = textareaSelectionToTextEditorSelection(
            textarea.value,
            textarea.selectionStart,
            textarea.selectionEnd,
            textarea.selectionDirection,
        );
        composingRef.current = false;
        textEditor.flushComposition(textarea.value, range);
    }, [textEditor]);

    // 退出或切换编辑会话时取消焦点恢复，避免旧菜单影响新的编辑目标。
    useEffect(() => () => disposeContextMenuRef.current?.(), [session?.id]);

    const exitEditing = useCallback(() => {
        flushTextareaComposition();
        return textEditor.exit();
    }, [flushTextareaComposition, textEditor]);

    /**
     * 处理编辑态的快捷键。
     *
     * @param event - 键盘事件
     * @returns 是否处理了快捷键
     */
    const handleShortcut = useCallback(
        (event: KeyboardEvent | ReactKeyboardEvent<HTMLTextAreaElement>) => {
            const current = textEditor.getSession();
            if (!current) return false;

            if (event.key === 'Escape') {
                event.preventDefault();
                exitEditing();
                return true;
            }

            const meta = event.metaKey || event.ctrlKey;
            const key = event.key.toLowerCase();

            if (meta && key === 'a') {
                event.preventDefault();
                const length = getUtf8ByteLength(current.adapter.getText(current.editingLayer));
                textEditor.setSelection({ start: 0, end: length });
                return true;
            }

            // 只接管撤销/重做快捷键，其他组合键交回浏览器或外层快捷键系统。
            if (!meta || (key !== 'z' && !(event.ctrlKey && key === 'y'))) return false;

            event.preventDefault();

            if (!composingRef.current) {
                if ((key === 'z' && event.shiftKey) || key === 'y') {
                    textEditor.redo();
                } else {
                    textEditor.undo();
                }
            }
            return true;
        },
        [exitEditing, textEditor],
    );

    /**
     * 处理 textarea 的输入变化。
     *
     * @param event - 输入事件
     */
    const handleChange = useCallback(
        (event: SyntheticEvent<HTMLTextAreaElement>) => {
            desiredInlineRef.current = null;

            const target = event.currentTarget;
            const selection = textareaSelectionToTextEditorSelection(
                target.value,
                target.selectionStart,
                target.selectionEnd,
                target.selectionDirection,
            );

            // ATTENTION: boundary 传参说明
            // 1. 在 composition 期间传 null，表示只同步中间值、不写普通输入历史；
            // 2. 在非 composition 期间传 undefined，让 updateText 自己计算普通输入的撤销边界。
            textEditor.updateText(target.value, selection, composingRef.current ? null : undefined);
        },
        [textEditor],
    );

    const handleCompositionStart = useCallback(() => {
        if (composingRef.current) return;
        composingRef.current = true;
        desiredInlineRef.current = null;
    }, []);

    const handleCompositionEnd = useCallback(() => {
        flushTextareaComposition();
    }, [flushTextareaComposition]);

    /**
     * 将活跃 textarea 的选区同步到 session。
     * 只同步活跃的 textarea，且 composition 期间不触发，避免输入法组合态的脏数据。
     *
     * @param textarea - native textarea
     */
    const syncTextareaSelection = useCallback(
        (textarea: HTMLTextAreaElement) => {
            if (composingRef.current || restoreContextMenuRef.current || document.activeElement !== textarea) return;

            textEditor.setSelection(
                textareaSelectionToTextEditorSelection(
                    textarea.value,
                    textarea.selectionStart,
                    textarea.selectionEnd,
                    textarea.selectionDirection,
                ),
            );
        },
        [textEditor],
    );

    const handleSelect = useCallback(
        (event: SyntheticEvent<HTMLTextAreaElement>) => {
            syncTextareaSelection(event.currentTarget);
        },
        [syncTextareaSelection],
    );

    const handleKeyUp = useCallback(
        (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
            syncTextareaSelection(event.currentTarget);
        },
        [syncTextareaSelection],
    );

    const handleCopy = useCallback(
        (event: ReactClipboardEvent<HTMLTextAreaElement> | ClipboardEvent) => {
            event.preventDefault();
            restoreContextMenuRef.current?.();

            if (composingRef.current || !event.clipboardData) return;

            const target = textareaRef.current;
            if (!target || target.selectionStart === target.selectionEnd) return;

            event.clipboardData.setData('text/plain', target.value.slice(target.selectionStart, target.selectionEnd));
            textEditor.copySelection(
                textareaSelectionToTextEditorSelection(
                    target.value,
                    target.selectionStart,
                    target.selectionEnd,
                    target.selectionDirection,
                ),
            );
        },
        [textEditor],
    );

    const handleCut = useCallback(
        (event: ReactClipboardEvent<HTMLTextAreaElement> | ClipboardEvent) => {
            event.preventDefault();
            restoreContextMenuRef.current?.();

            if (composingRef.current || !event.clipboardData) return;

            const target = textareaRef.current;
            if (!target) return;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            if (start === end) return;

            handleCopy(event);

            const nextText = target.value.slice(0, start) + target.value.slice(end);
            const selection = textareaSelectionToTextEditorSelection(nextText, start, start);

            target.value = nextText;
            target.setSelectionRange(start, start);

            textEditor.updateText(nextText, selection, 'clipboard');
        },
        [handleCopy, textEditor],
    );

    const handlePaste = useCallback(
        (event: ReactClipboardEvent<HTMLTextAreaElement> | ClipboardEvent) => {
            event.preventDefault();
            restoreContextMenuRef.current?.();

            if (composingRef.current) return;

            const pasted = event.clipboardData?.getData('text/plain');
            if (!pasted) return;

            const target = textareaRef.current;
            if (!target) return;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            const nextText = target.value.slice(0, start) + pasted + target.value.slice(end);
            const caret = start + pasted.length;
            const selection = textareaSelectionToTextEditorSelection(nextText, caret, caret);

            target.value = nextText;
            target.setSelectionRange(caret, caret);

            textEditor.updateText(nextText, selection, 'clipboard');
        },
        [textEditor],
    );

    /**
     * 处理 textarea 键盘输入，并只在几何相关的方向键操作前同步收敛排版。
     *
     * 普通按键继续读取当前 session，避免把全文 layout 放回输入热路径；方向键需要根据 writing mode、
     * caret 和目标行命中盒计算新偏移，因此必须先调用 `flushLayout()` 获取最新 snapshot。
     */
    const handleKeyDown = useCallback(
        (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
            const current = ARROW_KEYS.has(event.key) ? textEditor.flushLayout() : textEditor.getSession();
            if (!current) return;

            onKeyDown(event);

            if (
                !composingRef.current &&
                !event.nativeEvent.isComposing &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey &&
                (event.key === 'Tab' || event.key === 'Backspace')
            ) {
                const target = event.currentTarget;
                const start = target.selectionStart;
                const end = target.selectionEnd;
                const isTab = event.key === 'Tab';
                const deletesTab =
                    start === end && target.value.slice(Math.max(0, start - TAB_TEXT.length), start) === TAB_TEXT;

                if (isTab || deletesTab) {
                    event.preventDefault();
                    desiredInlineRef.current = null;

                    const replaceStart = isTab ? start : start - TAB_TEXT.length;
                    const inserted = isTab ? TAB_TEXT : '';
                    const nextText = target.value.slice(0, replaceStart) + inserted + target.value.slice(end);
                    const caret = replaceStart + inserted.length;
                    const selection = textareaSelectionToTextEditorSelection(nextText, caret, caret);

                    target.value = nextText;
                    target.setSelectionRange(caret, caret);
                    textEditor.updateText(nextText, selection, isTab ? 'punctuation' : undefined);
                    return;
                }
            }

            const vertical = current.snapshot?.writingMode === 'vertical-rl';
            const isInlineMove = vertical
                ? event.key === 'ArrowUp' || event.key === 'ArrowDown'
                : event.key === 'ArrowLeft' || event.key === 'ArrowRight';
            if (isInlineMove) desiredInlineRef.current = null;

            const handledArrow =
                ARROW_KEYS.has(event.key) &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey &&
                (vertical || event.key === 'ArrowUp' || event.key === 'ArrowDown');

            if (handledArrow) {
                const focus = current.selection.focus;
                const lineIndex = getLineIndexFromOffset(current.snapshot, focus);
                const caret = getCaretRect(current.snapshot, { start: focus, end: focus }, minHeight);
                if (caret && lineIndex >= 0) {
                    event.preventDefault();

                    let offset: number | null;
                    if (vertical && isInlineMove) {
                        offset = resolveAdjacentInlineOffset(
                            current.snapshot,
                            lineIndex,
                            focus,
                            event.key === 'ArrowUp' ? -1 : 1,
                        );
                    } else {
                        desiredInlineRef.current ??= vertical ? caret.y : caret.x;
                        const targetLineIndex = vertical
                            ? lineIndex + (event.key === 'ArrowLeft' ? 1 : -1)
                            : lineIndex + (event.key === 'ArrowUp' ? -1 : 1);
                        offset = resolveByteOffsetAtLineInline(
                            current.snapshot,
                            targetLineIndex,
                            desiredInlineRef.current,
                        );
                    }

                    if (offset !== null) {
                        if (event.shiftKey) {
                            const anchor = current.selection.anchor;
                            textEditor.setSelection({
                                start: Math.min(anchor, offset),
                                end: Math.max(anchor, offset),
                                anchor,
                                focus: offset,
                            });
                        } else {
                            textEditor.setSelection({ start: offset, end: offset });
                        }
                    }
                    return;
                }
            }
            handleShortcut(event);
        },
        [handleShortcut, minHeight, onKeyDown, textEditor],
    );

    /**
     * 用空文本或当前选中文字生成原生菜单，不让浏览器对正文重新命中选词
     *
     * 菜单输入框挂到 body，以客户端坐标定位，避开编辑框的旋转、缩放和裁剪。
     * 菜单形成后恢复正文焦点，复制、剪切、粘贴仍使用原来的输入处理。
     *
     * 此操作会临时创建菜单输入框并切换焦点
     *
     * @param point - 右键事件的客户端坐标
     */
    const prepareContextMenu = useCallback(
        (point: { clientX: number; clientY: number }) => {
            disposeContextMenuRef.current?.();
            flushTextareaComposition();

            const textarea = textareaRef.current;
            const current = textEditor.getSession();
            if (!textarea || !current) return;

            const text = current.adapter.getText(current.editingLayer);
            const selection = textEditorSelectionToTextareaSelection(text, current.selection);
            textarea.setSelectionRange(selection.start, selection.end, selection.direction);

            const doc = textarea.ownerDocument;
            const proxy = doc.createElement('textarea');

            proxy.className = 'text-editor__textarea text-editor__context-menu';
            proxy.tabIndex = -1;
            proxy.spellcheck = false;
            proxy.wrap = 'off';
            proxy.value = text.slice(selection.start, selection.end);
            // 留出命中边距，让鼠标落在代理内部而非边缘。
            proxy.style.left = `${point.clientX - 5}px`;
            proxy.style.top = `${point.clientY - 5}px`;

            proxy.oncontextmenu = (event) => event.stopPropagation();

            proxy.oncopy = (event) => {
                event.preventDefault();
                if (textEditor.getSession()?.id !== current.id) return;
                handleCopy(event);
            };

            proxy.oncut = (event) => {
                event.preventDefault();
                if (textEditor.getSession()?.id === current.id) handleCut(event);
            };

            proxy.onpaste = (event) => {
                event.preventDefault();
                if (textEditor.getSession()?.id === current.id) handlePaste(event);
            };

            doc.body.append(proxy);
            proxy.focus({ preventScroll: true });
            proxy.select();

            const restore = () => {
                clearTimeout(timer);
                proxy.style.pointerEvents = 'none';
                proxy.style.width = '1px';
                proxy.style.height = '1px';
                if (textarea.isConnected && textEditor.getSession()?.id === current.id && doc.activeElement === proxy) {
                    textarea.focus({ preventScroll: true });
                }
                restoreContextMenuRef.current = null;
            };

            // 浏览器先读取代理的文本和选区生成菜单；下一任务恢复正文焦点，不改正文的布局或内容。
            const timer = setTimeout(restore, 0);
            restoreContextMenuRef.current = restore;
            disposeContextMenuRef.current = () => {
                clearTimeout(timer);
                proxy.remove();
                restoreContextMenuRef.current = null;
                disposeContextMenuRef.current = null;
            };
        },
        [flushTextareaComposition, handleCopy, handleCut, handlePaste, textEditor],
    );

    useImperativeHandle(
        ref,
        () => ({
            exitEditing,
            prepareContextMenu,
            resetDesiredInline: () => {
                desiredInlineRef.current = null;
            },
        }),
        [exitEditing, prepareContextMenu],
    );

    /**
     * textarea 未聚焦时，仍允许编辑态响应 Escape、全选和撤销/重做。
     */
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (document.activeElement === textareaRef.current) return;
            handleShortcut(event);
        };

        window.addEventListener('keydown', handler);

        return () => window.removeEventListener('keydown', handler);
    }, [handleShortcut]);

    /**
     * 退出编辑时，刷新输入法组合输入。
     */
    useEffect(() => {
        return () => flushTextareaComposition();
    }, [flushTextareaComposition]);

    /**
     * 同步 textarea 和 session 的选区。
     */
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea || !session) {
            lastSessionIDRef.current = null;
            composingRef.current = false;
            return;
        }

        if (composingRef.current) return;

        const text = session.adapter.getText(session.editingLayer);
        const isNewSession = lastSessionIDRef.current !== session.id;

        lastSessionIDRef.current = session.id;
        textarea.value = text;

        // 新进入文本编辑态时必须强制聚焦 textarea，不能依赖双击画布后的浏览器自然失焦。
        //
        // 例如双击图层、快捷键或程序调用都可能创建新的编辑 session；进入成功后，
        // 键盘输入的目标就应该切到当前文本。此时即使 activeElement 仍是外部可编辑控件，
        // 也不能因为 shouldRestoreTextareaFocus 的避让逻辑而跳过首次聚焦。
        //
        // 同一个 session 的后续同步才使用 shouldRestoreTextareaFocus，
        // 避免用户主动编辑其他控件时被抢焦点。
        const selection = textEditorSelectionToTextareaSelection(text, session.selection);
        const shouldFocus = isNewSession || shouldRestoreTextareaFocus(document.activeElement, textarea);
        const selectionChanged =
            textarea.selectionStart !== selection.start ||
            textarea.selectionEnd !== selection.end ||
            textarea.selectionDirection !== selection.direction;

        if (shouldFocus) textarea.focus();
        if (selectionChanged) textarea.setSelectionRange(selection.start, selection.end, selection.direction);
    }, [session]);

    if (!session) return null;

    const text = session.adapter.getText(session.editingLayer);
    const style = session.adapter.getDisplayStyle(session.editingLayer);
    const geometry = session.adapter.getContentGeometry(session.editingLayer, session.snapshot);
    const inputCaretGeometry = getCaretRect(
        session.snapshot,
        { start: session.selection.focus, end: session.selection.focus },
        minHeight,
        geometry.emptyCaretX,
    );
    const vertical = session.snapshot?.writingMode === 'vertical-rl';

    // 隐藏 textarea 只有 1px 宽，默认软换行会让光标落在折行行首；旧 Chromium 在此前一个字符
    // 为空格时再次输入空格，会主动插入真实换行符，随后被 handleChange 写入图层，表现为连续空格不断换行。
    // 用 wrap="off" 关闭输入载体的软换行，保留用户主动输入的回车；文字排版仍由引擎负责。
    // 根因源码：https://chromium.googlesource.com/chromium/src/+/9cf9befa7d98c4cf2d6363024e24e680c234318f/third_party/blink/renderer/core/editing/editor.cc
    // Chromium 修复：https://chromium.googlesource.com/chromium/src/+/2a137720e54938bbf6c8ac35a03ba3c528cfa665
    // 问题记录：https://github.com/w3c/editing/issues/482
    return (
        <textarea
            ref={textareaRef}
            className="text-editor__textarea"
            wrap="off"
            defaultValue={text}
            style={{
                // 光标几何位于快照坐标系，需要加上宿主提供的内容框偏移才能落到图层局部坐标。
                top:
                    geometry.contentOffset.y +
                    (vertical
                        ? (inputCaretGeometry?.y ?? 0)
                        : (inputCaretGeometry?.y ?? 0) + (inputCaretGeometry?.height ?? minHeight)),
                left:
                    geometry.contentOffset.x +
                    (vertical
                        ? (inputCaretGeometry?.x ?? 0) + (inputCaretGeometry?.width ?? minHeight)
                        : (inputCaretGeometry?.x ?? 0)),
                fontFamily: style.fontFamily,
                fontSize: style.fontSize,
                lineHeight: style.lineSpacing,
                letterSpacing: style.letterSpacing,
                fontWeight: style.bold ? 'bold' : 'normal',
                fontStyle: style.italic ? 'italic' : 'normal',
            }}
            tabIndex={-1}
            spellCheck={false}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onSelect={handleSelect}
            onKeyUp={handleKeyUp}
            onCopy={handleCopy}
            onCut={handleCut}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
        />
    );
});

TextEditorTextarea.displayName = 'TextEditorTextarea';
